// /snapshot — public-ready board-meeting view.
//
// Single page. Top metrics + recent wins + upcoming closing grants +
// per-profile one-line summary. Auth-gated like the other authed pages
// (the snapshot displays real funding figures and partner names, so the
// AODA-clear public-facing surface is /governance, not /snapshot).
//
// Print-friendly via @media print so the browser's "Save as PDF" produces
// a clean board handout: chrome hidden, dense one-page layout.

import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { formatCAD, formatDate, formatClosing } from '../lib/format';

interface ProfileSnapshot {
  profile_id: string;
  profile_slug: string;
  profile_name: string;
  open_applications: number;
  committed_cad: number;
  awarded_ytd_cad: number;
  new_matches: number;
}
interface RecentWin {
  application_id: string;
  grant_title: string;
  funder_name: string;
  profile_name: string;
  awarded_amount_cad: number;
  decision_at: number;
}
interface UpcomingDeadline {
  grant_program_id: string;
  grant_title: string;
  funder_name: string;
  closes_at: number;
}
interface Snapshot {
  generated_at: number;
  window_months: number;
  verified_grants: number;
  open_applications: number;
  total_funding_ask_cad: number;
  total_match_committed_cad: number;
  wins_last_6m: number;
  awarded_last_6m_cad: number;
  new_matches_open: number;
  recent_wins: RecentWin[];
  upcoming_deadlines: UpcomingDeadline[];
  profiles: ProfileSnapshot[];
}

export default function SnapshotPage() {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['dashboards', 'snapshot'],
    queryFn: () => api.get<Snapshot>('/dashboards/snapshot'),
  });

  if (q.error instanceof ApiError && q.error.status === 401) {
    navigate({ to: '/login' });
    return null;
  }

  const data = q.data?.data;

  return (
    <div className="h-full overflow-y-auto bg-white print:overflow-visible">
      <div className="max-w-5xl mx-auto px-6 py-6 print:px-0 print:py-0">
        <header className="flex items-baseline justify-between gap-4 mb-5">
          <div>
            <h1 className="text-lg font-semibold text-neutral-900">Grants snapshot</h1>
            <p className="text-xs text-neutral-600 mt-1">
              {data ? `Generated ${formatDate(data.generated_at)} · ${data.window_months}-month rolling window` : '…'}
            </p>
          </div>
          <div className="print:hidden flex gap-2">
            <button
              type="button"
              onClick={() => window.print()}
              className="text-xs rounded-md border border-neutral-300 px-2 py-1 hover:bg-neutral-50"
            >Print or save as PDF</button>
          </div>
        </header>

        {q.isLoading && <p className="text-sm text-neutral-600">Loading snapshot — a moment.</p>}
        {q.error && !(q.error instanceof ApiError && q.error.status === 401) && (
          <p className="text-sm text-rose-700">Could not load snapshot.</p>
        )}

        {data && (
          <>
            <section aria-labelledby="snapshot-metrics" className="mb-6">
              <h2 id="snapshot-metrics" className="sr-only">Top metrics</h2>
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 print:grid-cols-4 print:gap-2">
                <Metric label="Open applications" value={data.open_applications.toLocaleString()} sub={`${formatCAD(data.total_funding_ask_cad)} requested`} />
                <Metric label="Committed match" value={formatCAD(data.total_match_committed_cad)} sub="across in-progress applications" />
                <Metric label={`Wins · ${data.window_months} mo`} value={data.wins_last_6m.toLocaleString()} sub={`${formatCAD(data.awarded_last_6m_cad)} awarded`} />
                <Metric label="New matches awaiting triage" value={data.new_matches_open.toLocaleString()} sub={`${data.verified_grants.toLocaleString()} verified grants`} />
              </dl>
            </section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 print:grid-cols-2 print:gap-4">
              <section aria-labelledby="snapshot-wins">
                <h2 id="snapshot-wins" className="text-sm font-semibold text-neutral-900 border-b border-neutral-200 pb-1 mb-2">
                  Recent wins
                </h2>
                {data.recent_wins.length === 0 ? (
                  <p className="text-xs text-neutral-500">No wins recorded in the last {data.window_months} months.</p>
                ) : (
                  <ul className="text-sm divide-y divide-neutral-100">
                    {data.recent_wins.map((w) => (
                      <li key={w.application_id} className="py-2 flex items-baseline justify-between gap-3">
                        <div>
                          <div className="font-medium text-neutral-900">{w.grant_title}</div>
                          <div className="text-xs text-neutral-600">{w.funder_name} · {w.profile_name} · {formatDate(w.decision_at)}</div>
                        </div>
                        <div className="text-sm font-mono text-neutral-900 shrink-0">{formatCAD(w.awarded_amount_cad)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section aria-labelledby="snapshot-upcoming">
                <h2 id="snapshot-upcoming" className="text-sm font-semibold text-neutral-900 border-b border-neutral-200 pb-1 mb-2">
                  Upcoming closing grants
                </h2>
                {data.upcoming_deadlines.length === 0 ? (
                  <p className="text-xs text-neutral-500">No verified grants closing in the next 90 days.</p>
                ) : (
                  <ul className="text-sm divide-y divide-neutral-100">
                    {data.upcoming_deadlines.map((g) => (
                      <li key={g.grant_program_id} className="py-2">
                        <div className="font-medium text-neutral-900">{g.grant_title}</div>
                        <div className="text-xs text-neutral-600">{g.funder_name} · {formatClosing(g.closes_at)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            <section aria-labelledby="snapshot-profiles" className="mt-6">
              <h2 id="snapshot-profiles" className="text-sm font-semibold text-neutral-900 border-b border-neutral-200 pb-1 mb-2">
                By profile
              </h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-neutral-600">
                    <th scope="col" className="py-1.5 pr-3">Profile</th>
                    <th scope="col" className="py-1.5 pr-3 text-right">Open apps</th>
                    <th scope="col" className="py-1.5 pr-3 text-right">Match committed</th>
                    <th scope="col" className="py-1.5 pr-3 text-right">Awarded ({data.window_months}mo)</th>
                    <th scope="col" className="py-1.5 pr-3 text-right">New matches</th>
                  </tr>
                </thead>
                <tbody>
                  {data.profiles.map((p) => (
                    <tr key={p.profile_id} className="border-t border-neutral-100">
                      <td className="py-1.5 pr-3 text-neutral-900">{p.profile_name}</td>
                      <td className="py-1.5 pr-3 text-right font-mono">{p.open_applications}</td>
                      <td className="py-1.5 pr-3 text-right font-mono">{formatCAD(p.committed_cad)}</td>
                      <td className="py-1.5 pr-3 text-right font-mono">{formatCAD(p.awarded_ytd_cad)}</td>
                      <td className="py-1.5 pr-3 text-right font-mono">{p.new_matches}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <footer className="mt-8 pt-3 border-t border-neutral-200 text-xs text-neutral-500 print:mt-4">
              <p>
                All figures pulled directly from the application database. No AI-generated content on this page.
                A human has verified every grant referenced here before it entered the matching engine.
              </p>
            </footer>
          </>
        )}
      </div>

      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          body { background: white !important; }
          /* Hide the global app shell — header / sidebar / chrome — so the
             snapshot prints alone. */
          header[class*="h-14"], nav, .print\\:hidden { display: none !important; }
          a { color: black; text-decoration: none; }
          .border-b { border-bottom: 1px solid #aaa; }
          .border-t { border-top: 1px solid #ddd; }
          h1 { font-size: 16pt; }
          h2 { font-size: 12pt; }
          body, td, th { font-size: 10pt; }
        }
      `}</style>
    </div>
  );
}

function Metric({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="border border-neutral-200 rounded-md p-3 print:p-2 print:border-neutral-300">
      <dt className="text-xs text-neutral-600">{label}</dt>
      <dd className="mt-1">
        <div className="text-xl font-semibold text-neutral-900 print:text-lg">{value}</div>
        {sub && <div className="text-xs text-neutral-500 mt-0.5">{sub}</div>}
      </dd>
    </div>
  );
}
